import React, { useState } from 'react';
import { AthleteProfile, OfflineQueueItem } from '../types';
import { 
  LayoutDashboard, 
  BarChart3, 
  MapPin, 
  Trophy, 
  MessageSquare, 
  User, 
  ShieldAlert, 
  PlusCircle, 
  Flame,
  CalendarCheck,
  UserPlus,
  Lock,
  Award,
  Film,
  Rss,
  RefreshCw,
  CloudUpload,
  CheckCircle2,
  WifiOff,
  Database,
  Sparkles,
  Swords,
  MoreHorizontal,
  X,
  Eye,
  Gift,
  Zap
} from 'lucide-react';

export type NavTab = 
  | 'dashboard' 
  | 'warmups'
  | 'feed'
  | 'scouts'
  | 'achievements' 
  | 'analytics' 
  | 'games' 
  | 'tournaments'
  | 'highlights'
  | 'map' 
  | 'leaderboard' 
  | 'volleyball_leaderboard'
  | 'chat' 
  | 'profile' 
  | 'admin'
  | 'superadmin'
  | 'auth'
  | 'reset-password';

interface NavigationProps {
  user?: AthleteProfile;
  activeTab: NavTab;
  setActiveTab: (tab: NavTab) => void;
  onOpenStatLog: () => void;
  onOpenHostGame: () => void;
  onOpenReferralModal?: () => void;
  unreadChatCount?: number;
  isLoggedIn?: boolean;
  pendingQueue?: OfflineQueueItem[];
  isSyncing?: boolean;
  syncProgress?: { current: number; total: number };
  isOnline?: boolean;
  onSimulateOfflineAction?: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({
  user,
  activeTab,
  setActiveTab,
  onOpenStatLog,
  onOpenHostGame,
  onOpenReferralModal,
  unreadChatCount = 0,
  isLoggedIn = true,
  pendingQueue = [],
  isSyncing = false,
  syncProgress = { current: 0, total: 0 },
  isOnline = true,
  onSimulateOfflineAction,
}) => {
  const [isMobileMoreOpen, setIsMobileMoreOpen] = useState(false);
  interface NavItem {
    id: NavTab;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    badge?: number;
  }

  const navItems: NavItem[] = [
    { id: 'dashboard', label: 'Home', icon: LayoutDashboard },
    { id: 'warmups', label: 'Warmups 🔥', icon: Flame },
    { id: 'feed', label: 'Social Feed', icon: Rss },
    { id: 'scouts', label: 'Scout Network 🔒', icon: Eye },
    { id: 'achievements', label: 'Achievements', icon: Award },
    { id: 'games', label: 'Pickup Games', icon: CalendarCheck },
    { id: 'tournaments', label: 'Tournaments', icon: Swords },
    { id: 'highlights', label: 'Reels & Challenges', icon: Film },
    { id: 'map', label: 'Courts & Maps', icon: MapPin },
    ...(user?.primarySport === 'volleyball'
      ? [{ id: 'volleyball_leaderboard' as NavTab, label: 'Volleyball Leaderboard 🏐', icon: Trophy }]
      : []),
    { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
    { id: 'analytics', label: 'Stats & Analytics', icon: BarChart3 },
    { id: 'chat', label: 'Team Chat', icon: MessageSquare, badge: unreadChatCount },
    { id: 'profile', label: 'Profile & Security', icon: User },
    { id: 'auth', label: 'Register & Sign In', icon: UserPlus },
  ];

  return (
    <>
      <aside className="hidden lg:flex flex-col w-64 bg-indigo-950/90 dark:bg-indigo-950/90 border-r border-white/10 p-4 space-y-6 shrink-0 min-h-[calc(100vh-4rem)] text-white">
        {/* Quick Action Buttons */}
        <div className="space-y-2">
          <button
            onClick={onOpenStatLog}
            className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-lime-400 hover:bg-lime-300 text-black font-black italic rounded-2xl shadow-lg shadow-lime-400/20 transition transform active:scale-95 text-sm uppercase tracking-wider"
          >
            <PlusCircle className="w-5 h-5 stroke-[2.5]" />
            <span>Log Game Stats</span>
          </button>

          <button
            onClick={onOpenHostGame}
            className="w-full flex items-center justify-center space-x-2 py-2.5 px-4 bg-rose-500 hover:bg-rose-600 text-white font-black italic rounded-2xl border border-rose-400/30 transition text-sm uppercase tracking-wider shadow-md shadow-rose-500/20"
          >
            <Flame className="w-4 h-4 fill-white/20" />
            <span>Host Pickup Game</span>
          </button>

          {onOpenReferralModal && (
            <button
              onClick={onOpenReferralModal}
              className="w-full flex items-center justify-center space-x-2 py-2 px-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-black italic rounded-2xl transition text-xs uppercase tracking-wider shadow-md shadow-amber-500/20"
            >
              <Gift className="w-4 h-4 stroke-[2.5]" />
              <span>Refer Friends (+150 XP)</span>
            </button>
          )}
        </div>

        {/* Level & XP Progress Card */}
        {isLoggedIn && user && (
          <div className="bg-indigo-900/60 p-3.5 rounded-2xl border border-white/10 space-y-2 shadow-inner">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-1.5 font-black italic text-lime-400 uppercase tracking-tight">
                <Trophy className="w-4 h-4 text-lime-400" />
                <span>LEVEL {user.level}</span>
              </div>
              <span className="text-[10px] font-mono font-bold text-indigo-200/80">
                {user.xp} / {user.xpToNextLevel} XP
              </span>
            </div>

            {/* Level Progress Bar with Smooth Transition Animation */}
            <div className="w-full bg-indigo-950 rounded-full h-2.5 overflow-hidden p-0.5 border border-white/10 relative">
              <div
                className="bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-300 h-full rounded-full transition-all duration-700 ease-out shadow-sm shadow-lime-400/40"
                style={{ width: `${Math.min(100, Math.round((user.xp / user.xpToNextLevel) * 100))}%` }}
              />
            </div>
          </div>
        )}

        {/* Visual Progress Bar & Status Indicator for OfflineQueueItem Sync */}
        {(isSyncing || (pendingQueue && pendingQueue.length > 0) || !isOnline) && (
          <div className={`p-3.5 rounded-2xl border transition-all duration-300 space-y-2.5 ${
            isSyncing 
              ? 'bg-indigo-900/90 border-lime-400/60 ring-2 ring-lime-400/20 shadow-lg shadow-lime-400/10'
              : !isOnline
              ? 'bg-amber-500/10 border-amber-400/30'
              : 'bg-indigo-900/40 border-white/10'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {isSyncing ? (
                  <RefreshCw className="w-4 h-4 text-lime-400 animate-spin" />
                ) : !isOnline ? (
                  <WifiOff className="w-4 h-4 text-amber-400 animate-pulse" />
                ) : (
                  <Database className="w-4 h-4 text-lime-400" />
                )}
                <span className="text-xs font-black italic uppercase tracking-wider text-white">
                  {isSyncing 
                    ? 'Adaptive Syncing...' 
                    : !isOnline 
                    ? 'Offline Queue' 
                    : 'Sync Ready'}
                </span>
              </div>

              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase ${
                isSyncing 
                  ? 'bg-lime-400 text-black'
                  : !isOnline
                  ? 'bg-amber-400 text-black'
                  : 'bg-indigo-800 text-indigo-200'
              }`}>
                {isSyncing 
                  ? `${Math.round(((syncProgress?.current || 0) / (syncProgress?.total || 1)) * 100)}%` 
                  : `${pendingQueue?.length || 0} Queued`}
              </span>
            </div>

            {/* Visual Progress Bar Component */}
            {isSyncing ? (
              <div className="space-y-1">
                <div className="flex justify-between items-center text-[10px] font-mono text-indigo-200">
                  <span>Adaptive SW Sync</span>
                  <span className="font-bold text-lime-400">
                    {syncProgress?.current || 0} / {syncProgress?.total || 0}
                  </span>
                </div>
                <div className="w-full bg-indigo-950 h-2.5 rounded-full overflow-hidden border border-white/10 p-0.5 relative">
                  <div
                    className="bg-gradient-to-r from-lime-400 to-emerald-400 h-full rounded-full transition-all duration-300 shadow-md shadow-lime-400/40 animate-pulse"
                    style={{
                      width: `${Math.min(100, Math.round(((syncProgress?.current || 0) / (syncProgress?.total || 1)) * 100))}%`,
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[10px] text-indigo-300/80 font-bold">
                  <span>{pendingQueue.length} record(s) in queue</span>
                  <div className="flex items-center space-x-2">
                    {onSimulateOfflineAction && (
                      <button
                        onClick={onSimulateOfflineAction}
                        className="text-lime-400 hover:underline font-black uppercase text-[9px]"
                      >
                        + Add Record
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                  {pendingQueue.map((item, idx) => (
                    <span
                      key={item.id || idx}
                      className={`text-[9px] font-mono px-1.5 py-0.5 rounded border flex items-center ${
                        item.status === 'failed'
                          ? 'bg-rose-500/20 border-rose-400/40 text-rose-300'
                          : item.status === 'syncing'
                          ? 'bg-lime-400/20 border-lime-400/40 text-lime-300 animate-pulse'
                          : 'bg-amber-400/10 border-amber-400/30 text-amber-300'
                      }`}
                    >
                      {item.type} {item.retryCount ? `(x${item.retryCount})` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!isSyncing && isOnline && pendingQueue.length === 0 && (
              <p className="text-[10px] text-indigo-200/70 font-medium flex items-center">
                <CheckCircle2 className="w-3 h-3 text-lime-400 mr-1 shrink-0" />
                All records synced to database.
              </p>
            )}
          </div>
        )}

        {/* Primary Nav Links */}
        <nav className="flex-1 space-y-1">
          <div className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-indigo-300/60">
            ATHLETE NAVIGATION
          </div>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const isLocked = !isLoggedIn && item.id !== 'auth';

            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id as NavTab)}
                className={`w-full flex items-center justify-between px-3.5 py-3 rounded-2xl text-sm transition-all ${
                  isActive
                    ? 'bg-indigo-900/90 text-lime-400 font-black italic border-l-4 border-lime-400 shadow-md'
                    : isLocked
                    ? 'text-indigo-300/40 hover:text-indigo-200 hover:bg-indigo-900/20 font-medium'
                    : 'text-indigo-200/70 hover:text-white hover:bg-indigo-900/40 font-bold'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-lime-400' : isLocked ? 'text-indigo-400/30' : 'text-indigo-300/60'}`} />
                  <span>{item.label}</span>
                </div>
                {isLocked ? (
                  <span title="Requires Login or Registration">
                    <Lock className="w-3.5 h-3.5 text-indigo-400/50" />
                  </span>
                ) : item.badge && item.badge > 0 ? (
                  <span className="px-2 py-0.5 text-[10px] font-black rounded-full bg-rose-500 text-white">
                    {item.badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>

        {/* Footer info */}
        <div className="pt-2 border-t border-white/10">
          <div className="p-3 bg-indigo-900/40 rounded-2xl border border-white/5 text-xs text-indigo-200/70 space-y-1">
            <div className="flex items-center justify-between font-bold text-white">
              <span className="font-black italic text-lime-400">PLAYGROUND OS</span>
              <span className="text-[10px] bg-lime-400/20 text-lime-300 px-2 py-0.5 rounded-full font-black">v2.4 Live</span>
            </div>
            <p className="text-[11px]">Real-time stat sync & geofence alerts active.</p>
          </div>
        </div>
      </aside>

      {/* Mobile Top Sync Progress Banner */}
      {(isSyncing || (pendingQueue && pendingQueue.length > 0)) && (
        <div className="lg:hidden fixed top-14 left-0 right-0 z-40 bg-indigo-950/95 border-b border-lime-400/30 px-3.5 py-2 backdrop-blur-md flex items-center justify-between text-xs text-white shadow-lg">
          <div className="flex items-center space-x-2">
            {isSyncing ? (
              <RefreshCw className="w-3.5 h-3.5 text-lime-400 animate-spin" />
            ) : (
              <WifiOff className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
            )}
            <span className="text-[11px] font-black italic uppercase tracking-tight">
              {isSyncing ? 'Syncing Offline Records...' : `${pendingQueue?.length || 0} Offline Item(s) Pending`}
            </span>
          </div>

          {isSyncing ? (
            <div className="flex items-center space-x-2">
              <span className="text-[10px] font-mono font-black text-lime-400">
                {syncProgress?.current}/{syncProgress?.total}
              </span>
              <div className="w-16 bg-indigo-900 h-2 rounded-full overflow-hidden border border-white/10 p-0.5">
                <div
                  className="bg-lime-400 h-full rounded-full transition-all duration-300"
                  style={{ width: `${Math.round(((syncProgress?.current || 0) / (syncProgress?.total || 1)) * 100)}%` }}
                />
              </div>
            </div>
          ) : (
            onSimulateOfflineAction && (
              <button
                onClick={onSimulateOfflineAction}
                className="px-2.5 py-1 bg-lime-400 text-black font-black text-[9px] uppercase rounded-lg shadow-sm"
              >
                + Add Test Record
              </button>
            )
          )}
        </div>
      )}

      {/* Mobile Slide-Up More Menu Sheet */}
      {isMobileMoreOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col justify-end animate-fadeIn">
          <div className="bg-indigo-950 border-t-2 border-lime-400 rounded-t-3xl p-5 space-y-4 max-h-[80vh] overflow-y-auto text-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-lime-400" />
                <h3 className="font-black italic uppercase tracking-wider text-sm">All App Views</h3>
              </div>
              <button
                onClick={() => setIsMobileMoreOpen(false)}
                className="p-1.5 bg-indigo-900 rounded-full text-indigo-300 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id as NavTab);
                      setIsMobileMoreOpen(false);
                    }}
                    className={`flex items-center space-x-2.5 p-3 rounded-2xl text-xs transition ${
                      isActive
                        ? 'bg-lime-400 text-black font-black italic shadow-lg shadow-lime-400/20'
                        : 'bg-indigo-900/60 text-indigo-200 hover:bg-indigo-800 font-bold'
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Quick Actions inside More menu */}
            <div className="pt-2 grid grid-cols-2 gap-2 border-t border-white/10">
              <button
                onClick={() => {
                  onOpenStatLog();
                  setIsMobileMoreOpen(false);
                }}
                className="flex items-center justify-center space-x-1.5 py-2.5 bg-lime-400 text-black font-black italic rounded-xl text-xs uppercase"
              >
                <PlusCircle className="w-4 h-4" />
                <span>Log Stats</span>
              </button>
              <button
                onClick={() => {
                  onOpenHostGame();
                  setIsMobileMoreOpen(false);
                }}
                className="flex items-center justify-center space-x-1.5 py-2.5 bg-rose-500 text-white font-black italic rounded-xl text-xs uppercase"
              >
                <Flame className="w-4 h-4" />
                <span>Host Game</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-indigo-950/95 border-t border-white/10 px-1.5 py-2 flex items-center justify-around shadow-2xl text-white">
        {navItems.slice(0, 4).map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id as NavTab)}
              className={`flex flex-col items-center justify-center p-1 rounded-xl text-[10px] transition ${
                isActive
                  ? 'text-lime-400 font-black italic scale-105'
                  : 'text-indigo-200/60 font-semibold hover:text-white'
              }`}
            >
              <Icon className="w-5 h-5 mb-0.5" />
              <span>{item.label.split(' ')[0]}</span>
            </button>
          );
        })}

        {/* Mobile Chat Tab */}
        <button
          onClick={() => setActiveTab('chat')}
          className={`relative flex flex-col items-center justify-center p-1 rounded-xl text-[10px] transition ${
            activeTab === 'chat' ? 'text-lime-400 font-black italic scale-105' : 'text-indigo-200/60 font-semibold'
          }`}
        >
          <MessageSquare className="w-5 h-5 mb-0.5" />
          <span>Chat</span>
          {unreadChatCount > 0 && (
            <span className="absolute top-0.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full" />
          )}
        </button>

        {/* Mobile More Sheet Toggle Button */}
        <button
          onClick={() => setIsMobileMoreOpen(true)}
          className={`flex flex-col items-center justify-center p-1 rounded-xl text-[10px] transition ${
            isMobileMoreOpen ? 'text-lime-400 font-black italic scale-105' : 'text-indigo-200/60 font-semibold hover:text-white'
          }`}
        >
          <MoreHorizontal className="w-5 h-5 mb-0.5" />
          <span>More</span>
        </button>
      </nav>
    </>
  );
};
